import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SeoMeta from '../../components/SeoMeta';
import CmsImage from '../../components/CmsImage';
import Lightbox from '../../components/Lightbox';
import ContactForm from '../../components/ContactForm';
import ProductCard from '../../components/ProductCard';
import { fetchCollection, fetchLocalizedCollection } from '../../lib/cms';
import { imageUrl, srcSet } from '../../lib/images';

const PRODUCT_DATA_EN = {
  'central-lubrication': {
    title: 'Central Lubrication',
    seoTitle: 'PMB Central Lubrication – Authorized SKF & Lincoln Distributor',
    metaDesc:
      'PMB Central Lubrication – SKF & Lincoln. Future lubrication solutions with 60+ years experience, nationwide assembly, and 2-year warranty.',
    shortDesc:
      'Future-proof lubrication solutions built on experience and innovation. With over 60 years of expertise, we know that automatic lubrication is the foundation of operational reliability, reduced downtime, and maximum machinery longevity.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/centralsmoering-1.webp',
    gallerySlug: 'galleri-centralsmoering',
    longDescHtml: `
      <p>At <strong>PMB</strong>, centralized lubrication is at the core of what we do. With more than 60 years of specialized experience, we know that lubrication is never just a minor detail – it is the cornerstone of equipment uptime, efficiency, and productivity. We combine our in-house engineering with the market's premier global brands, including <strong>SKF and Lincoln Lubrication Systems</strong>, delivering solutions that generate measurable value every single day.</p>
      
      <h3>Why centralized lubrication makes a decisive difference</h3>
      <ul>
        <li><strong>Optimized performance</strong> – with an automated centralized system from SKF or Lincoln, all friction bearings, bushings, and moving components receive precise, uniform lubrication without interrupting production.</li>
        <li><strong>Substantial cost savings</strong> – fewer unexpected breakdowns, eliminated manual greasing routines, and reduced lubricant waste result in lower total cost of ownership.</li>
        <li><strong>Extended machinery lifetime</strong> – continuous lubrication significantly reduces mechanical wear and extends bearing lifespan by up to 50%.</li>
        <li><strong>Sustainable and environmentally responsible</strong> – exact metered dosing prevents grease overflow, reducing lubricant consumption by up to 40%.</li>
      </ul>

      <h3>SKF & Lincoln Lubrication – World-Class Quality</h3>
      <p>At <strong>PMB</strong>, we collaborate closely with SKF Lubrication Systems and Lincoln. This enables us to engineer and deliver systems that are thoroughly field-proven and built for the most demanding environments – from heavy recycling and construction machinery to manufacturing presses and food production lines.</p>

      <h3>What you can expect from PMB</h3>
      <ul>
        <li><strong>Custom-engineered installations</strong> – we tailor each lubrication circuit to your specific machine geometry and duty cycle.</li>
        <li><strong>Full turnkey delivery</strong> – from design, calculation, and component selection to on-site piping installation and commissioning.</li>
        <li><strong>Nationwide mobile service</strong> – our equipped service vans are on-site within 24 hours nationwide.</li>
        <li><strong>Comprehensive warranty</strong> – we stand behind our workmanship with a 2-year warranty on all newly installed lubrication systems.</li>
      </ul>

      <h3>10 compelling reasons to choose centralized lubrication from PMB</h3>
      <ul>
        <li>Significantly improves machine productivity and continuous availability.</li>
        <li>Substantially extends bearing and bushing lifespan.</li>
        <li>Dramatically reduces recurring repair, maintenance, and replacement costs.</li>
        <li>Eliminates hazardous manual lubrication access points for machine operators.</li>
        <li>Saves up to 40% in lubricant consumption through pinpoint metering.</li>
        <li>Massive domestic stock of genuine SKF, Lincoln, and Vogel pumps, dividers, and fittings.</li>
        <li>More than 60 years of dedicated lubrication engineering experience.</li>
        <li>Authorized SKF & Lincoln certified distribution partner.</li>
        <li>Full nationwide emergency dispatch and preventive service plans.</li>
        <li>Comprehensive 2-year warranty coverage on complete systems installed by PMB.</li>
      </ul>
    `,
  },
  laser: {
    title: 'Laser Cutting',
    seoTitle: 'PMB Laser Cutting Machines – High-Precision Fiber Laser Systems',
    metaDesc:
      'High-precision fiber laser cutting machinery supplied and serviced by PMB. Advanced CNC laser processing for sheet metal manufacturers.',
    shortDesc:
      'Next-generation fiber laser cutting machines engineered for exceptional cutting speeds, micrometer accuracy, and reliable 24/7 industrial production.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/laser-1.webp',
    gallerySlug: 'galleri-laser',
    longDescHtml: `
      <p>PMB supplies industry-leading fiber laser systems engineered for modern sheet metal fabricators who demand pinpoint cutting accuracy and ultra-high processing speeds. With cutting powers ranging from 3 kW to 30 kW+, our fiber lasers tackle everything from ultra-thin precision foils to thick structural steel plates with flawless edge quality.</p>

      <h3>Key Advantages of PMB Laser Solutions</h3>
      <ul>
        <li><strong>Extreme productivity</strong> – cutting-edge linear drives deliver rapid acceleration and lightning-fast pierced cut transitions.</li>
        <li><strong>Low operational overhead</strong> – high electrical efficiency and minimal consumable wear compared to legacy CO2 machines.</li>
        <li><strong>Automated material handling</strong> – seamless integration with shuttle tables, sheet loaders, and automated sorting towers.</li>
        <li><strong>Complete lifecycle service</strong> – optical alignment, CNC programming support, and preventative laser health checks by certified PMB technicians.</li>
      </ul>
    `,
  },
  shears: {
    title: 'Guillotine Shears',
    seoTitle: 'PMB Industrial Guillotine Shears – Robust Hydraulic Cutting Machinery',
    metaDesc:
      'PMB hydraulic shears engineered for clean, burr-free sheet metal cutting. Variable rake angle, CNC backgauges, and robust steel frames.',
    shortDesc:
      'Industrial hydraulic shears providing high cutting capacity, distortion-free cuts, and rapid blade clearance adjustment for sheet metal fabricators.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/mvd-ishear-guillotine-shear.png',
    gallerySlug: 'galleri-saks',
    longDescHtml: `
      <p>Precision begins with a clean, distortion-free straight cut. PMB's hydraulic guillotine shears are constructed with rigid monoblock steel frames to withstand immense loads without deflection, delivering perfect edge geometry on every pass.</p>

      <h3>Features & Capabilities</h3>
      <ul>
        <li><strong>Variable rake angle</strong> – minimizes twisting and camber on narrow strips and thin sheet materials.</li>
        <li><strong>CNC high-speed backgauge</strong> – ball screw-driven backgauges provide rapid, repeatable positioning with 0.1 mm precision.</li>
        <li><strong>Fast blade gap adjustment</strong> – motorized or lever-adjusted blade clearance to match varying plate thicknesses in seconds.</li>
        <li><strong>Front sheet support systems</strong> – ergonomic front extensions with ball transfers to protect delicate polished sheets.</li>
      </ul>
    `,
  },
  'press-brakes': {
    title: 'Press Brakes',
    seoTitle: 'PMB CNC Press Brakes – Precision Sheet Metal Bending Machinery',
    metaDesc:
      'State-of-the-art CNC press brakes from PMB. Electric servo and hydraulic bending machines with high precision crowning and multi-axis backgauges.',
    shortDesc:
      'High-performance press brakes designed for maximum bending angle consistency, flexible tooling setups, and energy-efficient servo-hydraulic operation.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/kantpresser-1.webp',
    gallerySlug: 'galleri-kantpresser',
    longDescHtml: `
      <p>PMB offers a comprehensive lineup of state-of-the-art CNC press brakes tailored to demanding metal fabrication environments. From compact electric servo press brakes to heavy-duty tandem synchronized presses, our solutions combine rigid mechanical engineering with cutting-edge touch-screen CNC control.</p>

      <h3>Key Advantages</h3>
      <ul>
        <li><strong>Synchronized Y1/Y2 positioning</strong> – high-resolution linear encoders monitor ram travel independently on both sides for exact parallelism.</li>
        <li><strong>Dynamic CNC crowning</strong> – automated deflection compensation guarantees uniform bend angles across the entire working length.</li>
        <li><strong>Multi-axis backgauge automation</strong> – up to 6-axis (X, X-prime, R, Z1, Z2, Delta X) backgauge systems for complex multi-stage bending jobs.</li>
        <li><strong>Quick-clamp tooling systems</strong> – pneumatic or hydraulic tool clamping for rapid, safe tooling changeovers.</li>
      </ul>
    `,
  },
  equipment: {
    title: 'Equipment & Accessories',
    seoTitle: 'PMB Machinery Equipment & Tooling – Sheet Metal Fabrication Accessories',
    metaDesc:
      'Supplementary production equipment, tooling systems, handling cranes, and automation add-ons for presses and sheet metal processing.',
    shortDesc:
      'Comprehensive range of auxiliary equipment, safety enclosures, specialized tooling, and handling solutions to maximize manufacturing throughput.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/udstyr-1.webp',
    gallerySlug: 'galleri-udstyr',
    longDescHtml: `
      <p>Maximizing machine utilization requires the right auxiliary equipment. PMB offers an extensive catalog of factory-approved accessories, material handling lifts, safety light curtains, de-coilers, and specialized tooling designed to boost workshop safety and productivity.</p>
    `,
  },
  presses: {
    title: 'Industrial Presses',
    seoTitle: 'PMB Industrial Presses – Eccentric & Hydraulic Forming Presses',
    metaDesc:
      'Industrial eccentric and hydraulic presses built on PMB heritage since 1921. Complete overhauls, clutch-brake upgrades, and custom press lines.',
    shortDesc:
      'Heavy-duty eccentric and hydraulic stamping presses. Renowned for century-proven Danish durability, modern safety systems, and relentless reliability.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/presser-1.webp',
    gallerySlug: 'galleri-presser',
    longDescHtml: `
      <p>Since 1921, PMB eccentric presses have set the industry benchmark for durability and press longevity across European industry. We continue this legacy with advanced mechanical and hydraulic presses, combining time-tested cast and welded frames with modern pneumatic clutch-brake systems and state-of-the-art safety PLCs.</p>

      <h3>Press Capabilities & Upgrades</h3>
      <ul>
        <li><strong>C-frame and Straight-side presses</strong> – capacities from 25 to 1000+ metric tons tailored for stamping, blanking, and deep drawing.</li>
        <li><strong>Hydraulic and mechanical rebuilds</strong> – factory reconditioning, crankshaft precision grinding, and bronze bushing replacement.</li>
        <li><strong>Modern safety retrofits</strong> – dual safety valves, certified Category 4 light curtains, and CE-compliant electronic press controls.</li>
      </ul>
    `,
  },
  fiberlaser: {
    title: 'Fiber Laser Cutting',
    seoTitle: 'PMB Fiber Laser Systems – High-Precision Sheet Metal Cutting',
    metaDesc:
      'High-speed fiber laser cutting systems from PMB with advanced resonators and low energy consumption.',
    shortDesc:
      'State-of-the-art fiber laser cutting machines delivering exceptional cutting speeds, micrometer accuracy, and reliable 24/7 industrial production.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/bearbejdning-1.webp',
    gallerySlug: 'galleri-laser',
    longDescHtml: `
      <p>PMB supplies industry-leading fiber laser systems engineered for modern sheet metal fabricators who demand pinpoint cutting accuracy and ultra-high processing speeds.</p>
      <h3>Key Advantages</h3>
      <ul>
        <li><strong>High productivity:</strong> Fast cutting speeds and minimal piercing delay.</li>
        <li><strong>Low operating cost:</strong> High electrical efficiency and low consumable wear.</li>
        <li><strong>Automated handling:</strong> Shuttle table exchange systems and automatic loading options.</li>
      </ul>
    `,
  },
  roerlaser: {
    title: 'Tube Laser Cutting',
    seoTitle: 'PMB Tube Laser Cutting – Advanced 3D Profile & Tube Processing',
    metaDesc:
      'High-precision tube and profile laser cutting machinery from PMB for round, square, and open profiles.',
    shortDesc:
      'Advanced 3D tube laser cutting systems delivering seamless profiling, slotting, and beveling on round, rectangular, and custom sections.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/bearbejdning.png',
    gallerySlug: 'galleri-laser',
    longDescHtml: `
      <p>Tube laser processing eliminates multiple traditional manufacturing steps like sawing, drilling, and milling by executing all operations in one continuous automated cycle.</p>
    `,
  },
  'slibe-og-afgratemaskiner': {
    title: 'Grinding & Deburring Machines',
    seoTitle: 'PMB Grinding & Deburring Machines – Professional Edge Rounding',
    metaDesc:
      'Industrial deburring, deslagging, and edge rounding machines for laser-cut and punched sheet metal parts.',
    shortDesc:
      'Heavy-duty industrial deburring and edge rounding machinery for burr-free surfaces, oxide removal, and flawless coating prep.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/maskiner.jpg',
    longDescHtml: `
      <p>Effective deburring and edge rounding ensure safe handling and superior paint or powder coating adhesion on metal components.</p>
    `,
  },
  panelbukker: {
    title: 'Panel Benders',
    seoTitle: 'PMB Panel Benders – Automated Flexible Sheet Metal Bending',
    metaDesc:
      'Automatic panel benders for ultra-fast, scrap-free bending of panels, boxes, and sheet metal enclosures.',
    shortDesc:
      'Next-generation automated panel bending technology for rapid changeover, consistent angle precision, and minimal manual intervention.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/mvd-e-series.webp',
    longDescHtml: `
      <p>Automatic panel benders revolutionize sheet metal forming by manipulating and bending all four sides in an unbroken automated cycle.</p>
    `,
  },
  'hydraulisk-presse': {
    title: 'Hydraulic Presses',
    seoTitle: 'PMB Hydraulic Presses – Heavy-Duty Forming & Deep Drawing',
    metaDesc:
      'High-performance industrial hydraulic presses from Hidroliksan and European leaders for deep drawing, stamping, and assembly.',
    shortDesc:
      'Engineered hydraulic production presses offering rigid frame structures, proportional tonnage control, and flexible stroke settings.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/presser.png',
    longDescHtml: `
      <p>PMB supplies high-tonnage hydraulic production presses designed for demanding stamping, forming, deep drawing, and test operations.</p>
    `,
  },
  'mekanisk-presse': {
    title: 'Mechanical Presses',
    seoTitle: 'PMB Mechanical Presses – High-Speed Stamping & Eccentric Presses',
    metaDesc:
      'High-precision mechanical eccentric presses and stamping lines from PMB with unmatched reliability.',
    shortDesc:
      'Classic and contemporary mechanical presses engineered for rapid stroke rates, extreme mechanical stiffness, and lasting production uptime.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/maskiner.jpg',
    longDescHtml: `
      <p>Mechanical eccentric presses deliver the high speed and tonnage required for progressive stamping, punching, and blanking at volume.</p>
    `,
  },
  'styringer-og-software': {
    title: 'Controls & Software',
    seoTitle: 'PMB Machine Controls & Industrial Software Solutions',
    metaDesc:
      'Modern CNC press controls, safety retrofits, and smart manufacturing software for optimized press lines.',
    shortDesc:
      'Intelligent machine controllers, safety retrofitting modules, and Industry 4.0 software designed to maximize machine throughput and operator safety.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/fremforing-hero.webp',
    longDescHtml: `
      <p>Upgrade legacy control systems to modern CNC touchscreens with integrated safety diagnostics, tool recipe storage, and remote support.</p>
    `,
  },
  coilanlaeg: {
    title: 'Coil Processing Lines',
    seoTitle: 'PMB Coil Processing Lines & Auxiliary Equipment',
    metaDesc:
      'Complete automated coil feeding systems, decoilers, and straighteners from Dimeco and leading specialists.',
    shortDesc:
      'Heavy-duty industrial coil feed lines, straighteners, and decoilers engineered for continuous high-speed stamping operations.',
    image:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/epknx.jpg',
    longDescHtml: `
      <p>Turnkey coil feed solutions that prepare, uncoil, level, and accurately feed coiled strip directly into your press tools.</p>
    `,
  },
};

const SLUG_ALIAS_MAP = {
  centralsmoering: 'central-lubrication',
  'central-lubrication': 'central-lubrication',
  laser: 'fiberlaser',
  fiberlaser: 'fiberlaser',
  roerlaser: 'roerlaser',
  'slibe-og-afgratemaskiner': 'slibe-og-afgratemaskiner',
  slibemaskiner: 'slibe-og-afgratemaskiner',
  'grinding-machines': 'slibe-og-afgratemaskiner',
  pladeklipning: 'shears',
  saks: 'shears',
  shears: 'shears',
  panelbukker: 'panelbukker',
  'panel-benders': 'panelbukker',
  'hydraulisk-presse': 'hydraulisk-presse',
  'hydraulic-presses': 'hydraulisk-presse',
  kantpresse: 'press-brakes',
  kantpresser: 'press-brakes',
  kantbukker: 'press-brakes',
  'press-brakes': 'press-brakes',
  'mekanisk-presse': 'mekanisk-presse',
  'mechanical-presses': 'mekanisk-presse',
  'styringer-og-software': 'styringer-og-software',
  'controls-and-software': 'styringer-og-software',
  coilanlaeg: 'coilanlaeg',
  'coilanlaeg-og-foelgeudstyr': 'coilanlaeg',
  udstyr: 'equipment',
  equipment: 'equipment',
  presser: 'presses',
  presses: 'presses',
};

export default function ProductDetailPageEn({ specificSlug }) {
  const params = useParams();
  const rawSlug = specificSlug || params.slug;
  const canonicalSlug = SLUG_ALIAS_MAP[rawSlug] || rawSlug;

  const [cmsProduct, setCmsProduct] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState('ALL');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const fallbackProduct = PRODUCT_DATA_EN[canonicalSlug];

  useEffect(() => {
    setSelectedManufacturer('ALL');

    Promise.all([
      fetchLocalizedCollection('ydelser_produkter', 'en'),
      fetchLocalizedCollection('produkter', 'en'),
    ]).then(([items, machineProducts]) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.en || it.slug._ || it.slug.da || Object.values(it.slug)[0]
          : it?.slug;

      const match = (items || []).find((item) => {
        const sEn = getSlugVal(item);
        const sDa = typeof item.slug === 'object' ? item.slug.da : null;
        return (
          sEn === canonicalSlug ||
          sEn === rawSlug ||
          sDa === canonicalSlug ||
          sDa === rawSlug ||
          SLUG_ALIAS_MAP[sEn] === canonicalSlug ||
          SLUG_ALIAS_MAP[sDa] === canonicalSlug
        );
      });
      setCmsProduct(match || null);

      // Find machines belonging to this category
      const matchedCategoryProducts = (machineProducts || []).filter((item) => {
        const catTerms = item.terms?.produktkategori || [];
        const catField = Array.isArray(item.kategori) ? item.kategori : [item.kategori];
        const isMatch =
          catTerms.includes(rawSlug) ||
          catTerms.includes(canonicalSlug) ||
          catField.includes(rawSlug) ||
          catField.includes(canonicalSlug) ||
          (canonicalSlug === 'press-brakes' && (catTerms.includes('kantpresser') || !catTerms.length));
        return isMatch;
      });
      setCategoryProducts(matchedCategoryProducts);
    });
  }, [canonicalSlug, rawSlug]);

  const product = cmsProduct
    ? {
        title: cmsProduct.titel || fallbackProduct?.title || '',
        seoTitle: cmsProduct.seo_titel || fallbackProduct?.seoTitle || cmsProduct.titel || '',
        metaDesc: cmsProduct.meta_beskrivelse || fallbackProduct?.metaDesc || cmsProduct.kort_beskrivelse || '',
        shortDesc: cmsProduct.kort_beskrivelse || fallbackProduct?.shortDesc || '',
        image:
          (Array.isArray(cmsProduct.billede) ? cmsProduct.billede[0] : cmsProduct.billede) ||
          fallbackProduct?.image ||
          '',
        gallerySlug: cmsProduct.galleri_slug || fallbackProduct?.gallerySlug || '',
        heroImage:
          (Array.isArray(cmsProduct.hero_billede) ? cmsProduct.hero_billede[0] : cmsProduct.hero_billede) ||
          fallbackProduct?.heroImage ||
          '',
        longDescHtml: cmsProduct.lang_beskrivelse || fallbackProduct?.longDescHtml || '',
      }
    : fallbackProduct;

  useEffect(() => {
    if (product && product.gallerySlug) {
      fetchCollection('galleri').then((galleries) => {
        const matchedGal = (galleries || []).find((g) => {
          const gSlug = typeof g.slug === 'object' ? g.slug._ : g.slug;
          return gSlug === product.gallerySlug;
        });
        setGallery(matchedGal || null);
      });
    } else {
      setGallery(null);
    }
  }, [canonicalSlug, product?.gallerySlug]);

  // Dynamically extract manufacturers
  const availableManufacturers = useMemo(() => {
    const set = new Set();
    categoryProducts.forEach((p) => {
      const name = p.producent_tekst || p.terms?.producent?.[0] || '';
      if (name) set.add(name);
    });
    return Array.from(set);
  }, [categoryProducts]);

  // Filter products by selected manufacturer
  const filteredProducts = useMemo(() => {
    if (selectedManufacturer === 'ALL') return categoryProducts;
    return categoryProducts.filter((p) => {
      const name = p.producent_tekst || p.terms?.producent?.[0] || '';
      return name.trim().toLowerCase() === selectedManufacturer.trim().toLowerCase();
    });
  }, [categoryProducts, selectedManufacturer]);

  if (!product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-stone-50 px-4 text-center">
        <h1 className="text-2xl font-bold text-stone-900">
          Product or Service Not Found
        </h1>
        <p className="text-stone-600 mt-2 text-sm">
          The requested product page could not be located.
        </p>
        <Link
          to="/en"
          className="mt-6 px-6 py-2.5 rounded bg-[#006368] text-white font-semibold text-sm hover:bg-[#004d51] transition"
        >
          Return to Home
        </Link>
      </div>
    );
  }

  const galleryImages =
    gallery && Array.isArray(gallery.billeder) ? gallery.billeder : [];

  return (
    <>
      <SeoMeta
        title={product.seoTitle}
        description={product.metaDesc}
        ogImage={product.image}
      />

      {/* Header breadcrumb bar */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/en" className="hover:text-[#006368] transition">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="text-stone-500">Products</span>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">{product.title}</span>
        </div>
      </div>

      {/* Hero Banner for Product */}
      <section className="relative bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800 overflow-hidden">
        {product.heroImage && (
          <>
            <img
              src={imageUrl(product.heroImage, 'width=1920,quality=80,format=auto')}
              srcSet={srcSet(product.heroImage, [800, 1200, 1920])}
              sizes="100vw"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
            />
            {/* Dark overlay: balanced so the background image is visible while text remains crisp */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a2024]/80 via-[#1a2024]/65 to-[#1a2024]/40 pointer-events-none" />
          </>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
                Product Category
              </span>
              <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
                {product.title}
              </h1>
              {product.shortDesc && (
                <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed max-w-2xl">
                  {product.shortDesc}
                </p>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-4">
                {categoryProducts.length > 0 && (
                  <a
                    href="#products-grid"
                    className="px-6 py-3.5 rounded bg-[#006368] hover:bg-[#004d51] text-white font-bold text-sm tracking-wide transition shadow-sm flex items-center gap-2"
                  >
                    <span>View Machines ({categoryProducts.length})</span>
                    <i className="fa-solid fa-arrow-down text-xs" />
                  </a>
                )}
                <a
                  href="#contact-form"
                  className="px-6 py-3.5 rounded bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition flex items-center gap-2"
                >
                  <i className="fa-solid fa-envelope text-[#0d8086]" />
                  <span>Request Information</span>
                </a>
                <a
                  href="tel:57526262"
                  className="px-6 py-3.5 rounded bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition flex items-center gap-2"
                >
                  <i className="fa-solid fa-phone text-[#0d8086]" />
                  <span>Call +45 57 52 62 62</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Content Column */}
            <div className="lg:col-span-8 space-y-12">
              {/* Detailed Description */}
              {product.longDescHtml && (
                <div
                  className="prose prose-stone max-w-none text-stone-800 leading-relaxed
                    prose-headings:text-stone-900 prose-headings:font-bold prose-headings:tracking-tight
                    prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                    prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                    prose-p:mb-4 prose-p:text-base prose-p:leading-relaxed
                    prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                    prose-li:mb-2 prose-li:text-stone-700
                    prose-strong:text-stone-900 prose-strong:font-bold"
                  dangerouslySetInnerHTML={{ __html: product.longDescHtml }}
                />
              )}

              {/* PRODUCTS SECTION FOR THIS CATEGORY */}
              {categoryProducts.length > 0 && (
                <div id="products-grid" className="pt-8 border-t border-stone-200 scroll-mt-20">
                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#006368] mb-1 block">
                        Catalog
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900">
                        Machinery & Models
                      </h2>
                      <p className="text-sm text-stone-600 mt-1">
                        Find the ideal machine for your manufacturing needs. Click a product for full specifications.
                      </p>
                    </div>

                    {/* Dynamic Manufacturer Filter */}
                    {availableManufacturers.length > 0 && (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">
                          Manufacturer:
                        </span>
                        <div className="flex flex-wrap items-center gap-1.5 p-1 bg-stone-100 rounded-lg border border-stone-200">
                          <button
                            type="button"
                            onClick={() => setSelectedManufacturer('ALL')}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                              selectedManufacturer === 'ALL'
                                ? 'bg-[#006368] text-white shadow-sm'
                                : 'text-stone-700 hover:text-stone-900 hover:bg-stone-200/70'
                            }`}
                          >
                            All ({categoryProducts.length})
                          </button>
                          {availableManufacturers.map((m) => {
                            const count = categoryProducts.filter((p) => {
                              const name = p.producent_tekst || p.terms?.producent?.[0] || '';
                              return name.trim().toLowerCase() === m.trim().toLowerCase();
                            }).length;

                            return (
                              <button
                                key={m}
                                type="button"
                                onClick={() => setSelectedManufacturer(m)}
                                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                                  selectedManufacturer.toLowerCase() === m.toLowerCase()
                                    ? 'bg-[#006368] text-white shadow-sm'
                                    : 'text-stone-700 hover:text-stone-900 hover:bg-stone-200/70'
                                }`}
                              >
                                {m} ({count})
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Product Grid */}
                  {filteredProducts.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {filteredProducts.map((item) => (
                        <ProductCard key={item.id} product={item} lang="en" />
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center bg-stone-50 rounded-lg border border-stone-200">
                      <p className="text-stone-600 text-sm">
                        No machinery found for manufacturer "{selectedManufacturer}".
                      </p>
                      <button
                        type="button"
                        onClick={() => setSelectedManufacturer('ALL')}
                        className="mt-3 text-xs font-bold text-[#006368] underline"
                      >
                        Reset filter and show all machines
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Contact form section */}
              <div id="contact-form" className="pt-10 border-t border-stone-200">
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-stone-900">
                    Inquire about {product.title}
                  </h2>
                  <p className="text-sm text-stone-600 mt-1">
                    Have questions regarding specifications, delivery time, or tailored tooling? Contact our experts.
                  </p>
                </div>
                <ContactForm defaultSubject={`Inquiry regarding ${product.title}`} />
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="lg:col-span-4 space-y-8">
              {/* Other categories navigation */}
              <div className="bg-stone-50 rounded-lg border border-stone-200 p-6">
                <h3 className="text-sm font-bold uppercase tracking-wider text-stone-900 mb-4 flex items-center gap-2">
                  <i className="fa-solid fa-layer-group text-[#006368]" />
                  <span>Other Product Areas</span>
                </h3>
                <div className="space-y-1">
                  {[
                    { slug: 'press-brakes', label: 'Press Brakes' },
                    { slug: 'central-lubrication', label: 'Central Lubrication' },
                    { slug: 'laser', label: 'Laser Cutting' },
                    { slug: 'shears', label: 'Guillotine Shears' },
                    { slug: 'equipment', label: 'Equipment' },
                    { slug: 'presses', label: 'Presses' },
                  ].map((cat) => {
                    const isActive = cat.slug === canonicalSlug;
                    return (
                      <Link
                        key={cat.slug}
                        to={`/en/${cat.slug}`}
                        className={`block px-3 py-2.5 rounded text-sm font-medium transition ${
                          isActive
                            ? 'bg-[#006368] text-white font-bold'
                            : 'text-stone-700 hover:bg-stone-200/60 hover:text-[#006368]'
                        }`}
                      >
                        {cat.label}
                      </Link>
                    );
                  })}
                  <Link
                    to="/en/suppliers"
                    className="block px-3 py-2.5 rounded text-sm font-medium text-stone-700 hover:bg-stone-200/60 hover:text-[#006368] transition"
                  >
                    Suppliers
                  </Link>
                </div>
              </div>

              {/* Quick Contact Card */}
              <div className="bg-[#1a2024] text-white rounded-lg p-6 space-y-4">
                <h3 className="font-bold text-base text-white">Need Engineering Advice?</h3>
                <p className="text-sm text-stone-300">
                  Our technical team is ready to assist with sizing, tooling and custom machine specifications.
                </p>
                <div className="pt-2 space-y-3">
                  <a
                    href="tel:57526262"
                    className="flex items-center gap-3 p-3 rounded bg-white/10 hover:bg-white/20 transition text-sm font-bold whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-[#0d8086]" />
                    <span className="whitespace-nowrap">+45 57 52 62 62</span>
                  </a>
                  <a
                    href="mailto:info@pmborup.dk"
                    className="flex items-center gap-3 p-3 rounded bg-white/10 hover:bg-white/20 transition text-sm font-medium whitespace-nowrap"
                  >
                    <i className="fa-solid fa-envelope text-[#0d8086]" />
                    <span className="whitespace-nowrap">info@pmborup.dk</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        images={galleryImages.length > 0 ? galleryImages : product.image ? [product.image] : []}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
